/** Space above a sought transcript message, clear of the top fade and resize handle. */
const TRANSCRIPT_SCROLL_TARGET_OFFSET_PX = 48;

/** Keep distant seeks from animating through the entire transcript. */
const MAX_SMOOTH_SCROLL_DISTANCE_PX = 240;

/** A message's first fragment rests with its whole message, so the speaker line above it stays in view; a later fragment rests by itself. */
function findScrollTarget(element: Element): Element {
  const message = element.previousElementSibling
    ? null
    : element.closest("[data-transcript-message]");
  return message ?? element;
}

/** The scroll position that rests an element just below the top of its container, within the scrollable range. */
export function computeIdealScrollTopForElement(
  container: Element,
  element: Element,
): number {
  const maxScrollTop = Math.max(
    0,
    container.scrollHeight - container.clientHeight,
  );
  const elementTop =
    findScrollTarget(element).getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop;

  return Math.max(
    0,
    Math.min(elementTop - TRANSCRIPT_SCROLL_TARGET_OFFSET_PX, maxScrollTop),
  );
}

/**
 * Set to "system" on a container while `scrollToTranscriptElement` scrolls it, so audio following can tell that scroll
 * from the user's. Following clears it once the scroll ends.
 */
export const SCROLLING_ROLE_DATA_KEY = "arScrollingRole";

export function scrollTranscriptContainer(
  container: HTMLElement,
  top: number,
  behavior: ScrollBehavior = "smooth",
): void {
  // Nothing scrolls when the container already rests there, so no scroll end would clear the mark
  if (Math.abs(top - container.scrollTop) < 1) {
    return;
  }

  container.dataset[SCROLLING_ROLE_DATA_KEY] = "system";
  const distance = top - container.scrollTop;
  if (
    behavior === "smooth" &&
    Math.abs(distance) > MAX_SMOOTH_SCROLL_DISTANCE_PX
  ) {
    container.scrollTo({
      top: top - Math.sign(distance) * MAX_SMOOTH_SCROLL_DISTANCE_PX,
      behavior: "instant",
    });
  }
  container.scrollTo({ top, behavior });
}

export function scrollToTranscriptElement(
  container: HTMLElement,
  element: Element,
  behavior: ScrollBehavior = "smooth",
): void {
  const top = computeIdealScrollTopForElement(container, element);
  scrollTranscriptContainer(container, top, behavior);
}

export function findClosestScrollContainer(
  element: HTMLElement,
): HTMLElement | null {
  let ancestor: HTMLElement | null = element.parentElement;

  while (ancestor && ancestor.scrollHeight <= ancestor.clientHeight) {
    ancestor = ancestor.parentElement;
  }

  return ancestor;
}
